import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import VoterCardFrontImage from '../../Assets/images/voter_card_2.png';
import TakeDocumentPhoto, {
    ITakeDocumentPhotoInstructions
} from '../../Components/TakeDocumentPhoto/TakeDocumentPhoto';
import DI from '../../../Utils/DI';
import Constants, {
    DOCUMENT_ORIENTATION,
    DOCUMENT_TYPES
} from '../../../Utils/Constants';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import IDocumentManager from '../../../Business/IDocumentManager';
import IUserManager from '../../../Business/UserManager/IUserManager';
import IChallengeManager from '../../../Business/IChallengeManager';
import useGaEventTracker from '../../Hooks/useGaEventTracker';
import IAnalyticsManager from '../../../Business/IAnalyticsManager';
import useStepContext from '../../Hooks/useStepContext';
import Utils from '../../../Utils/Utils';
import { ISteps } from '../../../Utils/Constants/FormSteps';
import ISessionManager from '../../../Business/ISessionManager';
import DocumentManager from '../../../Business/DocumentManager';
import CustomerRegistrationManager from '../../../Business/RegistrationManager/CustomerRegistrationManager';
import AgentRegistrationManager from '../../../Business/RegistrationManager/AgentRegistrationManager';
import { ERROR_CODES, ERRORS_DESCRIPTION } from '../../../Utils/Constants/Errors';
import { ITempError } from '../AgentLogin/agent-login.types';

interface IAdditionalDocumentProps extends IFormStepProps {
    currentDocIndex?: number;
    lastStep?: boolean
}

const AdditionalDocument: FC<IAdditionalDocumentProps> = ({ nextStepHandler, currentDocIndex, lastStep }) => {
    const { t } = useTranslation();
    const documentManager: IDocumentManager = DI.get('DocumentManager');
    const userManager: IUserManager = DI.get('UserManager');
    const challengeManager: IChallengeManager = DI.get('ChallengeManager');
    const { screenTracker, eventTracker } = useGaEventTracker();
    const takeDocumentPhotoInstructions: ITakeDocumentPhotoInstructions = {
        key: 'additionalDocument',
        instructionsLength: 4
    };

    const [error, setError] = useState<ITempError>({
        hasError: false,
        errorCode: NaN
    });

    const analyticsManager: IAnalyticsManager = DI.get('AnalyticsManager');
    const [additionalDocs, setAdditionalDocs] = useState<string[]>(() => {
        const existing = documentManager.getProcessedDocuments()
            .filter((d) => d.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase())
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        return existing.map((doc) => doc.file);
    });
    const { currentFormStep, whitelistedUser } = useStepContext();
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const isAgentLogged: boolean | undefined = useMemo(
        () => sessionManager.getSession()?.getIsValid(),
        []
    );
    const whitelistedResponse = CustomerRegistrationManager.getWhiteListedResponse();
    const agentWhitelistedRepsonse = AgentRegistrationManager.getAgentWhiteListedResponse();
    const [nextIndex, setNextIndex] = useState<number>(() => {
        const existing = documentManager.getProcessedDocuments()
            .filter((d) => d.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase());
        if (existing.length === 0) return 0;
        const max = Math.max(...existing.map((d) => d.index ?? 0));
        return max + 1;
    });
    const isMetaDataFrom = currentFormStep.KEY.includes(Constants.UTILS.METADATA);
    const onChangeFunction = (file: File, index?: number) => {
        const slotIndex = documentManager.getCurrentAdditionalIndex() ?? additionalDocs.length;
        if (typeof currentDocIndex !== 'number') {
            setNextIndex((prev) => prev + 1);
        }
        documentManager
            .processAdditionalDocumentData(
                String(userManager.getMsisdn()),
                file,
                DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase(),
                DOCUMENT_ORIENTATION.FRONT,
                whitelistedUser,
                slotIndex
            )
            .then(
                () => {
                    const uploadedDocs = documentManager.getProcessedDocuments()
                        .filter((doc) => doc.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase());

                    setAdditionalDocs((prev) => {
                        const updated = [...prev];
                        updated[slotIndex] = URL.createObjectURL(file);
                        return updated;
                    });
                    documentManager.setCurrentAdditionalIndex(null);

                    const elapsedTime = moment()
                        .subtract(
                            moment.unix(analyticsManager.getDocumentUploadInitTime()).unix(),
                            'milliseconds'
                        )
                        .format('mm:ss');

                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `TOC: ${currentFormStep.KEY} - ${Utils.getTimeInterval(elapsedTime)}`
                        }
                    });

                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `ATTEMPTS - ${currentFormStep.KEY} - ${analyticsManager.getDocumentUploadAttemptsCounter()}`
                        }
                    });

                    analyticsManager.setDocumentUploadAttemptsCounter(1);
                    if (typeof currentDocIndex === 'number') {
                        nextStepHandler({
                            ...Constants.FORM_STEPS.ADDITIONAL_DOCUMENT,
                            PROPS: { currentDocIndex: slotIndex }
                        });
                    } else if (whitelistedUser) {
                        const { fromStepKey } = currentFormStep.PROPS ?? {};
                        switch (fromStepKey) {
                        case Constants.FORM_STEPS.REVIEW_OTHERS.KEY:
                            nextStepHandler(Constants.FORM_STEPS.REVIEW_OTHERS);
                            break;
                        case Constants.FORM_STEPS.METADATA_OTHERS.KEY:
                            nextStepHandler(Constants.FORM_STEPS.METADATA_OTHERS);
                            break;
                        default:
                            nextStepHandler(Constants.FORM_STEPS.METADATA_OTHERS);
                        }
                    } else {
                        nextStepHandler(Constants.FORM_STEPS.STANDARD_REVIEW_DETAILS);
                    }
                }
            ).catch((errorCode) => {
                const numericCode = Number(errorCode);
                const isServerDown = [500, 503, 504].includes(numericCode);
                if (isServerDown) {
                    ERRORS_DESCRIPTION[numericCode] = {
                        title: t('error.serviceUnavailable'),
                        description: t('error.serviceUnavailableSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: numericCode
                    });
                    return;
                }
                if (!numericCode || isNaN(numericCode)) {
                    ERRORS_DESCRIPTION[ERROR_CODES.NETWORK_ERROR] = {
                        title: t('error.networkError'),
                        description: t('error.networkErrorSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: ERROR_CODES.NETWORK_ERROR
                    });
                    return;
                }

                setError((prev) => ({ ...prev, hasError: true, errorCode }));
            });
    };
    return (
        <>
            <TakeDocumentPhoto
                title={t('screenLabels.additionalDocument')}
                image={VoterCardFrontImage}
                instructionText={t('takePhoto.moveAndCenterDocument')}
                captureTitle={t('othersFront.captureTitle')}
                validateTitle={t('othersFront.validateTitle')}
                buttonText={t('othersFront.buttonText')}
                onChangeFn={onChangeFunction}
                takeDocumentPhotoInstructions={takeDocumentPhotoInstructions}
            />
        </>
    );
};

export default AdditionalDocument;
